<template>
  <div class="container">
    <loading :active.sync="submitting" :is-full-page="true" />

    <form @submit.prevent="submitForm">
      <div class="row">
        <!-- Name -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Name <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              v-model="form.name"
              placeholder="Enter your full name"
              required
            />
          </div>
        </div>

        <!-- Institution -->
        <div class="col-md-6">
          <div class="form-group">
            <label>
              Institution / Department
              <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              v-model="form.institution_department"
              placeholder="Enter institution or department name"
              required
            />
          </div>
        </div>

        <!-- City -->
        <div class="col-md-6">
          <div class="form-group">
            <label>City <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              v-model="form.city"
              placeholder="Enter your city"
              required
            />
          </div>
        </div>

        <!-- WhatsApp -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Whatsapp Number <span class="text-danger">*</span></label>

            <div class="d-flex">
              <!-- Country Code -->
              <div style="width: 40%; margin-right: 8px">
                <v-select
                  :options="countries"
                  label="dial_code"
                  :reduce="(c) => c.dial_code"
                  v-model="form.country_code"
                  placeholder="Country"
                >
                  <template #option="option">
                    {{ option.name }} (+{{ option.dial_code }})
                  </template>
                  <template #selected-option="option">
                    {{ option.code }} +{{ option.dial_code }}
                  </template>
                </v-select>
              </div>

              <!-- Phone Number -->
              <div style="width: 60%">
                <input
                  type="number"
                  class="form-control"
                  v-model="form.phone_no"
                  placeholder="WhatsApp number"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <!-- WhatsApp -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Email <span class="text-danger">*</span></label>
            <input
              type="email"
              class="form-control"
              v-model="form.email"
              placeholder="Enter your email"
              :readonly="emailVerified"
              :disabled="emailVerified"
              @blur="onEmailBlur"
              @input="onEmailInput"
              required
            />
            <small class="text-muted d-block">Ensure Email ID is working and correct</small>
            <small v-if="hasValidEmail && !emailChecked" class="text-muted d-block">
              Tab out of this field to load pricing for your email domain.
            </small>

            <div v-if="emailChecked && requiresOtp && !emailVerified" class="mt-2">
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                :disabled="sendingOtp"
                @click="sendOtp"
              >
                {{ sendingOtp ? "Sending…" : otpSent ? "Resend verification code" : "Send verification code" }}
              </button>
            </div>

            <div v-if="emailChecked && requiresOtp && otpSent && !emailVerified" class="mt-3">
              <label>Verification code <span class="text-danger">*</span></label>
              <div class="d-flex">
                <input
                  type="text"
                  class="form-control mr-2"
                  v-model="otp"
                  maxlength="4"
                  placeholder="Enter 4-digit code"
                />
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="verifyingOtp || otp.length !== 4"
                  @click="verifyOtp"
                >
                  {{ verifyingOtp ? "Verifying…" : "Verify" }}
                </button>
              </div>
              <small class="text-muted">Check your inbox for the verification code.</small>
            </div>

            <small v-if="requiresOtp && emailVerified" class="text-success d-block mt-1">
              Email verified successfully
            </small>
          </div>
        </div>

        <!-- Paper Title -->
        <div class="col-md-6" v-if="showTitleOfPaper">
          <div class="form-group">
            <label>Title of Paper</label>
            <input
              type="text"
              class="form-control"
              rows="3"
              v-model="form.title_of_paper"
              placeholder="Enter the title of your paper"
            />
          </div>
        </div>

        <!-- Designation -->
        <div class="col-md-6" v-if="showDesignation">
          <div class="form-group">
            <label>Designation</label>
            <input
              type="text"
              class="form-control"
              v-model="form.designation"
              placeholder="Enter your designation"
            />
          </div>
        </div>

        <!-- Mode of Presentation -->
        <div class="col-md-6" v-if="showModeOfPresentation">
          <div class="form-group">
            <label>Mode of Presentation <span class="text-danger">*</span></label>
            <select class="form-control" v-model="form.mode_of_presentation" required>
              <option value="" disabled>Select mode of presentation</option>
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </div>

        <!-- Register No -->
        <div class="col-md-6" v-if="showRegisterNo">
          <div class="form-group">
            <label>
              Register No
              <span class="text-danger" v-if="registerNoRequired">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              v-model="form.register_no"
              placeholder="Enter register number"
              :required="registerNoRequired"
            />
          </div>
        </div>

        <!-- Class / Batch -->
        <div class="col-md-6" v-if="showClassBatch">
          <div class="form-group">
            <label>
              Class / Batch
              <span class="text-danger" v-if="classBatchRequired">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              v-model="form.class_batch"
              placeholder="Enter class or batch"
              :required="classBatchRequired"
            />
          </div>
        </div>

        <!-- Custom fields -->
        <div
          v-for="field in customFields"
          :key="field.id"
          :class="field.type === 'select' ? 'col-12' : 'col-md-6'"
        >
          <div class="form-group" v-if="field.type === 'select'">
            <label>
              {{ field.label }}
              <span class="text-danger" v-if="field.required">*</span>
            </label>
            <div class="custom-select-choices" role="radiogroup" :aria-label="field.label">
              <label
                v-for="option in selectChoices(field)"
                :key="field.id + '-' + option"
                class="custom-select-choice"
                :class="{ 'is-selected': answerValue(field) === option }"
              >
                <input
                  type="radio"
                  :name="'custom_field_' + field.id"
                  :value="option"
                  v-model="form.custom_answers[field.id].value"
                  :required="field.required"
                />
                <span class="custom-select-choice-text">{{ option }}</span>
              </label>
            </div>
            <input
              v-if="isOtherSelected(field)"
              type="text"
              class="form-control mt-2"
              v-model="form.custom_answers[field.id].other"
              :placeholder="'Please specify ' + (field.other_label || 'Other')"
              required
            />
          </div>

          <div class="form-group" v-else>
            <label>
              {{ field.label }}
              <span class="text-danger" v-if="field.required">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              v-model="form.custom_answers[field.id].value"
              :placeholder="'Enter ' + field.label"
              :required="field.required"
            />
          </div>
        </div>

        <div class="col-md-6" v-for="field in uploadFields" :key="field.key">
          <div class="form-group">
            <label>
              {{ field.label }}
              <span class="text-danger" v-if="field.required">*</span>
            </label>

            <div
              class="register-upload"
              :class="{
                'is-filled': !!form[field.key],
                'is-dragging': dragField === field.key,
              }"
              @dragover.prevent="dragField = field.key"
              @dragleave="dragField = null"
              @drop.prevent="onFileDrop($event, field)"
            >
              <input
                :id="'upload-' + field.key"
                type="file"
                class="register-file-input"
                :accept="field.accept"
                :required="field.required && !form[field.key]"
                @change="onFileChange($event, field.key, field.maxKb, field.accept)"
              />

              <div v-if="form[field.key]" class="register-upload-selected">
                <div v-if="filePreviews[field.key]" class="register-upload-thumb">
                  <img :src="filePreviews[field.key]" :alt="form[field.key].name" />
                </div>
                <div v-else class="register-upload-badge">
                  <i class="far fa-file-alt"></i>
                </div>
                <div class="register-upload-meta">
                  <strong>{{ form[field.key].name }}</strong>
                  <span>{{ formatFileSize(form[field.key].size) }}</span>
                </div>
                <button
                  type="button"
                  class="register-upload-remove"
                  title="Remove file"
                  @click.prevent.stop="clearFile(field.key)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <label v-else class="register-upload-empty" :for="'upload-' + field.key">
                <span class="register-upload-badge">
                  <i class="fas fa-cloud-upload-alt"></i>
                </span>
                <span class="register-upload-meta">
                  <strong>Choose file</strong>
                  <span>or drag and drop here</span>
                </span>
              </label>
            </div>
            <small class="text-muted d-block">{{ mimeHint(field.accept) }}. Max {{ field.maxKb }} KB.</small>
          </div>
        </div>
      </div>

      <!-- Pricing -->
      <div v-if="emailChecked && requiresOtp && hasValidEmail && !emailVerified" class="alert alert-info mt-2">
        Verify your email with the OTP to unlock the pricing categories for your email by the institution.
      </div>

      <div v-else-if="emailChecked && pricingFetched && pricingList.length === 0" class="alert alert-warning mt-2">
        No pricing categories are available for your email domain.
      </div>

      <div class="row" v-if="pricingList.length">
        <!-- Category -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Category <span class="text-danger">*</span></label>
            <select class="form-control" v-model="selectedCategory" required>
              <option value="">Select Category</option>
              <option v-for="cat in categories" :key="cat" :value="cat">
                {{ cat }}
              </option>
            </select>
          </div>
        </div>

        <!-- Sub Category -->
        <div class="col-md-6">
          <div class="form-group">
            <label>Sub Category <span class="text-danger">*</span></label>
            <select
              class="form-control"
              v-model="selectedSubCategory"
              :disabled="!selectedCategory"
              required
            >
              <option value="">Select Sub Category</option>
              <option v-for="sub in subCategories" :key="sub" :value="sub">
                {{ sub }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- GST-INCLUSIVE PRICE BREAKUP -->
      <div v-if="selectedPricing" class="alert alert-info mt-2">
        <template v-if="isFreePricing">
          <div class="d-flex justify-content-between">
            <strong>Total Payable</strong>
            <strong class="text-success">Free</strong>
          </div>
        </template>
        <template v-else>
          <div class="d-flex justify-content-between">
            <span>Base Amount</span>
            <strong>₹{{ baseAmount }}</strong>
          </div>

          <div v-if="isGstApplicable" class="d-flex justify-content-between">
            <span>GST ({{ gstRate }}%)</span>
            <strong>₹{{ gstAmount }}</strong>
          </div>

          <hr />

          <div class="d-flex justify-content-between">
            <strong>Total Payable</strong>
            <strong class="text-success" v-if="isFreePricing">Free</strong>
            <strong class="text-success" v-else>₹{{ totalAmount }}</strong>
          </div>
        </template>

        <small v-if="selectedPricing.notes" class="text-muted d-block">
          {{ selectedPricing.notes }}
        </small>
      </div>

      <!-- reCAPTCHA -->
      <div class="form-group mt-3">
        <div
          class="g-recaptcha"
          :data-sitekey="captchaKey"
          data-callback="onCaptchaVerified"
        ></div>

        <small v-if="captchaError" class="text-danger">
          Please verify that you are not a robot.
        </small>
      </div>

      <!-- Submit -->
      <button type="submit" class="btn btn-primary mt-2">
        <i class="fa fa-check-circle"></i> Submit
      </button>
    </form>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    captchaKey: {
      type: String,
      required: true,
    },
    pricingApi: {
      type: String,
      required: true,
    },
    eventRegister: {
      type: String,
      required: true,
    },
    sendOtpUrl: {
      type: String,
      required: true,
    },
    verifyOtpUrl: {
      type: String,
      required: true,
    },
    showTitleOfPaper: {
      type: Boolean,
      default: true,
    },
    showDesignation: {
      type: Boolean,
      default: true,
    },
    showModeOfPresentation: {
      type: Boolean,
      default: false,
    },
    showPassportPhoto: {
      type: Boolean,
      default: false,
    },
    passportPhotoRequired: {
      type: Boolean,
      default: false,
    },
    passportPhotoMaxKb: {
      type: Number,
      default: 0,
    },
    passportPhotoMimes: {
      type: String,
      default: ".jpg,.jpeg,.png",
    },
    showAttachment1: {
      type: Boolean,
      default: false,
    },
    attachment1Required: {
      type: Boolean,
      default: false,
    },
    attachment1MaxKb: {
      type: Number,
      default: 0,
    },
    attachment1Mimes: {
      type: String,
      default: ".pdf,.jpg,.jpeg,.png,.doc,.docx",
    },
    showAttachment2: {
      type: Boolean,
      default: false,
    },
    attachment2Required: {
      type: Boolean,
      default: false,
    },
    attachment2MaxKb: {
      type: Number,
      default: 0,
    },
    attachment2Mimes: {
      type: String,
      default: ".pdf,.jpg,.jpeg,.png,.doc,.docx",
    },
    showAttachment3: {
      type: Boolean,
      default: false,
    },
    attachment3Required: {
      type: Boolean,
      default: false,
    },
    attachment3MaxKb: {
      type: Number,
      default: 0,
    },
    attachment3Mimes: {
      type: String,
      default: ".pdf,.jpg,.jpeg,.png,.doc,.docx",
    },
    showRegisterNo: {
      type: Boolean,
      default: false,
    },
    registerNoRequired: {
      type: Boolean,
      default: false,
    },
    showClassBatch: {
      type: Boolean,
      default: false,
    },
    classBatchRequired: {
      type: Boolean,
      default: false,
    },
    customFields: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      submitting: false,
      pricingLoading: false,
      captchaToken: null,
      captchaError: false,

      pricingList: [],
      pricingFetched: false,
      selectedCategory: "",
      selectedSubCategory: "",
      countries: [],
      countriesLoaded: false,
      otp: "",
      otpSent: false,
      emailVerified: false,
      emailChecked: false,
      lastPricingEmail: "",
      requiresOtp: false,
      sendingOtp: false,
      verifyingOtp: false,
      dragField: null,
      filePreviews: {
        passport_photo: null,
        attachment_1: null,
        attachment_2: null,
        attachment_3: null,
      },

      form: {
        name: "",
        email: "",
        institution_department: "",
        city: "",
        title_of_paper: "",
        designation: "",
        mode_of_presentation: "",
        country_code: "",
        phone_no: "",
        emergency_phone_no: "",
        register_no: "",
        class_batch: "",
        custom_answers: (Array.isArray(this.customFields) ? this.customFields : []).reduce(
          function (answers, field) {
            answers[field.id] = { value: "", other: "" };
            return answers;
          },
          {}
        ),
        passport_photo: null,
        attachment_1: null,
        attachment_2: null,
        attachment_3: null,
      },
    };
  },

  mounted() {
    window.onCaptchaVerified = this.onCaptchaVerified;
    this.loadCountries();
  },

  beforeDestroy() {
    Object.keys(this.filePreviews).forEach((field) => this.clearPreview(field));
  },

  computed: {
    hasValidEmail() {
      const email = (this.form.email || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    categories() {
      return [...new Set(this.pricingList.map((p) => p.category_name))];
    },

    subCategories() {
      return this.pricingList
        .filter((p) => p.category_name === this.selectedCategory)
        .map((p) => p.sub_category_name)
        .filter((v, i, a) => a.indexOf(v) === i);
    },

    selectedPricing() {
      return this.pricingList.find(
        (p) =>
          p.category_name === this.selectedCategory &&
          p.sub_category_name === this.selectedSubCategory
      );
    },

    isFreePricing() {
      return (
        this.selectedPricing &&
        (this.selectedPricing.is_free ||
          Number(this.selectedPricing.amount) === 0)
      );
    },

    isGstApplicable() {
      return this.selectedPricing && this.selectedPricing.is_gst_applicable;
    },

    gstRate() {
      return this.selectedPricing ? Number(this.selectedPricing.gst_rate) : 0;
    },

    totalAmount() {
      return this.selectedPricing ? Number(this.selectedPricing.amount) : 0;
    },

    // GST-INCLUSIVE reverse calculation
    baseAmount() {
      if (!this.isGstApplicable) return this.totalAmount;

      return Math.round((this.totalAmount * 100) / (100 + this.gstRate));
    },

    gstAmount() {
      if (!this.isGstApplicable) return 0;
      return this.totalAmount - this.baseAmount;
    },

    uploadFields() {
      const fields = [];
      if (this.showPassportPhoto) {
        fields.push({
          key: "passport_photo",
          label: "Passport size photo",
          required: this.passportPhotoRequired,
          maxKb: this.passportPhotoMaxKb,
          accept: this.passportPhotoMimes,
        });
      }
      if (this.showAttachment1) {
        fields.push({
          key: "attachment_1",
          label: "Attachment 1",
          required: this.attachment1Required,
          maxKb: this.attachment1MaxKb,
          accept: this.attachment1Mimes,
        });
      }
      if (this.showAttachment2) {
        fields.push({
          key: "attachment_2",
          label: "Attachment 2",
          required: this.attachment2Required,
          maxKb: this.attachment2MaxKb,
          accept: this.attachment2Mimes,
        });
      }
      if (this.showAttachment3) {
        fields.push({
          key: "attachment_3",
          label: "Attachment 3",
          required: this.attachment3Required,
          maxKb: this.attachment3MaxKb,
          accept: this.attachment3Mimes,
        });
      }
      return fields;
    },
  },

  watch: {
    selectedCategory() {
      this.selectedSubCategory = "";
    },
  },

  methods: {
    clearOtpState() {
      this.otp = "";
      this.otpSent = false;
      this.emailVerified = false;
      this.requiresOtp = false;
      this.emailChecked = false;
      this.selectedCategory = "";
      this.selectedSubCategory = "";
    },

    async loadCountries() {
      try {
        const res = await axios.get(this.pricingApi);
        if (res.data.countries && res.data.countries.length) {
          this.countries = res.data.countries;
          this.countriesLoaded = true;
        }
        if (!this.form.country_code) {
          this.form.country_code = "91";
        }
        this.pricingList = res.data.data || [];
        this.pricingFetched = true;
      } catch (e) {
        console.error("Failed to load countries", e);
      }
    },

    onEmailInput() {
      const email = (this.form.email || "").trim();
      if (email !== this.lastPricingEmail) {
        this.emailChecked = false;
        this.otpSent = false;
        this.emailVerified = false;
        this.otp = "";
        this.requiresOtp = false;
      }
    },

    async onEmailBlur() {
      const email = (this.form.email || "").trim();

      if (!this.hasValidEmail) {
        if (this.lastPricingEmail) {
          this.lastPricingEmail = "";
          this.clearOtpState();
          await this.loadCountries();
        }
        return;
      }

      if (email === this.lastPricingEmail) {
        return;
      }

      this.lastPricingEmail = email;
      this.clearOtpState();
      await this.fetchPricing(email);
      this.emailChecked = true;
    },

    async sendOtp() {
      if (!this.hasValidEmail) {
        Swal.fire("Please enter a valid email address", "", "info");
        return;
      }

      this.sendingOtp = true;
      try {
        await axios.post(this.sendOtpUrl, {
          email: this.form.email.trim(),
          name: this.form.name || "Delegate",
        });
        this.otpSent = true;
        this.emailVerified = false;
        Swal.fire("Verification code sent", "Check your email inbox.", "success");
      } catch (error) {
        this.otpSent = false;
        const message =
          error.response?.data?.message ||
          "Unable to send verification code.";
        Swal.fire(message, "", "warning");
      } finally {
        this.sendingOtp = false;
      }
    },

    async verifyOtp() {
      if (!this.hasValidEmail || this.otp.length !== 4) {
        return;
      }

      this.verifyingOtp = true;
      try {
        await axios.post(this.verifyOtpUrl, {
          email: this.form.email.trim(),
          otp: this.otp,
        });
        this.emailVerified = true;
        await this.fetchPricing(this.form.email.trim());
        Swal.fire("Email verified", "Domain-restricted pricing is now available.", "success");
      } catch (error) {
        const message =
          error.response?.data?.message ||
          "Invalid or expired verification code.";
        Swal.fire(message, "", "warning");
      } finally {
        this.verifyingOtp = false;
      }
    },

    async fetchPricing(email) {
      this.pricingLoading = true;
      try {
        const params = {};
        const resolvedEmail = (email || this.form.email || "").trim();
        if (resolvedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resolvedEmail)) {
          params.email = resolvedEmail;
        }

        const res = await axios.get(this.pricingApi, { params });
        this.pricingList = res.data.data || [];
        if (res.data.countries && res.data.countries.length) {
          this.countries = res.data.countries;
          this.countriesLoaded = true;
        }
        if (!this.form.country_code) {
          this.form.country_code = "91";
        }
        this.requiresOtp = !!res.data.requires_otp;
        if (res.data.otp_verified) {
          this.emailVerified = true;
        }
        this.pricingFetched = true;

        if (
          this.selectedCategory &&
          !this.categories.includes(this.selectedCategory)
        ) {
          this.selectedCategory = "";
          this.selectedSubCategory = "";
        } else if (
          this.selectedSubCategory &&
          !this.subCategories.includes(this.selectedSubCategory)
        ) {
          this.selectedSubCategory = "";
        }
      } catch (e) {
        console.error("Pricing API error", e);
        this.pricingList = [];
        this.pricingFetched = true;
      } finally {
        this.pricingLoading = false;
      }
    },

    onCaptchaVerified(token) {
      this.captchaToken = token;
      this.captchaError = false;
    },

    onFileChange(event, field, maxKb, accept) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
      this.assignFile(field, file, maxKb, accept, event.target);
    },

    onFileDrop(event, field) {
      this.dragField = null;
      const file = event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null;
      this.assignFile(field.key, file, field.maxKb, field.accept);
    },

    assignFile(field, file, maxKb, accept, inputEl) {
      if (file && !this.fileHasAllowedType(file, accept)) {
        Swal.fire(`Allowed types: ${this.mimeHint(accept)}`, "", "info");
        this.clearFile(field, inputEl);
        return;
      }
      if (file && this.fileExceedsLimit(file, maxKb)) {
        Swal.fire(`File must be ${maxKb} KB or smaller`, "", "info");
        this.clearFile(field, inputEl);
        return;
      }
      this.form[field] = file;
      this.setFilePreview(field, file);
    },

    clearFile(field, inputEl) {
      this.form[field] = null;
      this.clearPreview(field);
      const input = inputEl || document.getElementById("upload-" + field);
      if (input) {
        input.value = "";
      }
    },

    setFilePreview(field, file) {
      this.clearPreview(field);
      if (file && file.type && file.type.indexOf("image/") === 0) {
        this.$set(this.filePreviews, field, URL.createObjectURL(file));
      }
    },

    clearPreview(field) {
      const url = this.filePreviews[field];
      if (url) {
        URL.revokeObjectURL(url);
      }
      this.$set(this.filePreviews, field, null);
    },

    formatFileSize(bytes) {
      const size = Number(bytes) || 0;
      if (size < 1024) {
        return size + " B";
      }
      return (size / 1024).toFixed(size >= 102400 ? 0 : 1) + " KB";
    },

    mimeHint(accept) {
      const labels = (accept || "")
        .split(",")
        .map((part) => part.trim().replace(/^\./, "").toUpperCase())
        .filter(Boolean);
      return labels.length ? labels.join(", ") : "the allowed file types";
    },

    fileHasAllowedType(file, accept) {
      if (!file || !accept) {
        return true;
      }
      const allowed = accept
        .split(",")
        .map((part) => part.trim().replace(/^\./, "").toLowerCase())
        .filter(Boolean);
      if (!allowed.length) {
        return true;
      }
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      return allowed.includes(ext);
    },

    fileExceedsLimit(file, maxKb) {
      if (!file || !maxKb) {
        return false;
      }
      return file.size > Number(maxKb) * 1024;
    },

    blankCustomAnswers() {
      const answers = {};
      (this.customFields || []).forEach((field) => {
        answers[field.id] = { value: "", other: "" };
      });
      return answers;
    },

    selectChoices(field) {
      const options = Array.isArray(field.options) ? field.options.filter(Boolean) : [];
      const otherLabel = field.other_label || "Other";
      if (field.allow_other && options.indexOf(otherLabel) === -1) {
        return options.concat(otherLabel);
      }
      return options;
    },

    answerValue(field) {
      const answer = this.form.custom_answers[field.id];
      return answer ? answer.value : "";
    },

    isOtherSelected(field) {
      return (
        field.allow_other &&
        this.answerValue(field) === (field.other_label || "Other")
      );
    },

    emptyRegistrationForm() {
      return {
        name: "",
        email: "",
        institution_department: "",
        city: "",
        title_of_paper: "",
        designation: "",
        mode_of_presentation: "",
        country_code: this.form.country_code || "",
        phone_no: "",
        emergency_phone_no: "",
        register_no: "",
        class_batch: "",
        custom_answers: this.blankCustomAnswers(),
        passport_photo: null,
        attachment_1: null,
        attachment_2: null,
        attachment_3: null,
      };
    },

    extraFieldError() {
      if (this.showRegisterNo && this.registerNoRequired && !this.form.register_no) {
        return "Please enter your register number";
      }
      if (this.showClassBatch && this.classBatchRequired && !this.form.class_batch) {
        return "Please enter your class / batch";
      }
      const fields = this.customFields || [];
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        const answer = this.form.custom_answers[field.id] || {};
        if (field.required && !answer.value) {
          return field.type === "select"
            ? "Please select " + field.label
            : "Please enter " + field.label;
        }
        if (this.isOtherSelected(field) && !answer.other) {
          return "Please specify " + (field.other_label || "Other");
        }
      }
      if (this.showPassportPhoto && this.passportPhotoRequired && !this.form.passport_photo) {
        return "Please upload a passport size photo";
      }
      if (this.showPassportPhoto && this.fileExceedsLimit(this.form.passport_photo, this.passportPhotoMaxKb)) {
        return `Passport photo must be ${this.passportPhotoMaxKb} KB or smaller`;
      }
      if (this.showPassportPhoto && !this.fileHasAllowedType(this.form.passport_photo, this.passportPhotoMimes)) {
        return `Passport photo must be ${this.mimeHint(this.passportPhotoMimes)}`;
      }
      if (this.showAttachment1 && this.attachment1Required && !this.form.attachment_1) {
        return "Please upload Attachment 1";
      }
      if (this.showAttachment1 && this.fileExceedsLimit(this.form.attachment_1, this.attachment1MaxKb)) {
        return `Attachment 1 must be ${this.attachment1MaxKb} KB or smaller`;
      }
      if (this.showAttachment1 && !this.fileHasAllowedType(this.form.attachment_1, this.attachment1Mimes)) {
        return `Attachment 1 must be ${this.mimeHint(this.attachment1Mimes)}`;
      }
      if (this.showAttachment2 && this.attachment2Required && !this.form.attachment_2) {
        return "Please upload Attachment 2";
      }
      if (this.showAttachment2 && this.fileExceedsLimit(this.form.attachment_2, this.attachment2MaxKb)) {
        return `Attachment 2 must be ${this.attachment2MaxKb} KB or smaller`;
      }
      if (this.showAttachment2 && !this.fileHasAllowedType(this.form.attachment_2, this.attachment2Mimes)) {
        return `Attachment 2 must be ${this.mimeHint(this.attachment2Mimes)}`;
      }
      if (this.showAttachment3 && this.attachment3Required && !this.form.attachment_3) {
        return "Please upload Attachment 3";
      }
      if (this.showAttachment3 && this.fileExceedsLimit(this.form.attachment_3, this.attachment3MaxKb)) {
        return `Attachment 3 must be ${this.attachment3MaxKb} KB or smaller`;
      }
      if (this.showAttachment3 && !this.fileHasAllowedType(this.form.attachment_3, this.attachment3Mimes)) {
        return `Attachment 3 must be ${this.mimeHint(this.attachment3Mimes)}`;
      }
      return null;
    },

    buildRegistrationPayload() {
      const payload = new FormData();
      payload.append("name", this.form.name);
      payload.append("email", this.form.email);
      payload.append("institution_department", this.form.institution_department);
      payload.append("city", this.form.city);
      payload.append("phone_no", this.form.phone_no);
      payload.append("country_code", this.form.country_code);
      payload.append("title_of_paper", this.form.title_of_paper || "");
      payload.append("designation", this.form.designation || "");
      payload.append("mode_of_presentation", this.form.mode_of_presentation || "");
      payload.append("pricing_id", this.selectedPricing.pricing_id);
      payload.append("base_amount", this.baseAmount);
      payload.append("gst_rate", this.gstRate);
      payload.append("gst_amount", this.gstAmount);
      payload.append("total_amount", this.totalAmount);
      payload.append("recaptcha_token", this.captchaToken);

      if (this.showRegisterNo) {
        payload.append("register_no", this.form.register_no || "");
      }
      if (this.showClassBatch) {
        payload.append("class_batch", this.form.class_batch || "");
      }
      (this.customFields || []).forEach((field) => {
        const answer = this.form.custom_answers[field.id] || {};
        payload.append(`custom_answers[${field.id}][value]`, answer.value || "");
        if (field.type === "select") {
          payload.append(
            `custom_answers[${field.id}][other]`,
            this.isOtherSelected(field) ? answer.other || "" : ""
          );
        }
      });
      if (this.showPassportPhoto && this.form.passport_photo) {
        payload.append("passport_photo", this.form.passport_photo);
      }
      if (this.showAttachment1 && this.form.attachment_1) {
        payload.append("attachment_1", this.form.attachment_1);
      }
      if (this.showAttachment2 && this.form.attachment_2) {
        payload.append("attachment_2", this.form.attachment_2);
      }
      if (this.showAttachment3 && this.form.attachment_3) {
        payload.append("attachment_3", this.form.attachment_3);
      }

      return payload;
    },

    async submitForm() {
      // --- Simple frontend validation ---
      if (
        !this.form.name ||
        !this.form.institution_department ||
        !this.form.city ||
        !this.form.phone_no
      ) {
        Swal.fire("Please fill all required fields", "", "info");
        return;
      }

      if (!this.hasValidEmail) {
        Swal.fire("Please enter a valid email address", "", "info");
        return;
      }

      if (
        this.selectedPricing &&
        Number(this.selectedPricing.is_restricted) === 1 &&
        !this.emailVerified
      ) {
        Swal.fire("Please verify your email with the OTP sent to your inbox.", "", "info");
        return;
      }

      if (!this.selectedPricing) {
        Swal.fire("Please select category and sub category", "", "info");
        return;
      }

      const extraError = this.extraFieldError();
      if (extraError) {
        Swal.fire(extraError, "", "info");
        return;
      }

      if (!this.captchaToken) {
        this.captchaError = true;
        return;
      }

      this.submitting = true;

      try {
        const response = await axios.post(this.eventRegister, this.buildRegistrationPayload());
        console.log(response.data);
        if (response.data.is_free) {
          Swal.fire({
            title: "Registration Successful",
            text: "Registration successful",
            icon: "success",
          }).then((result) => {
            if (result.isConfirmed && response.data.redirect_url) {
              window.location.href = response.data.redirect_url;
            }
          });
        } else {
          Swal.fire({
            title: "Registration Successful",
            text:
              response.data.message ||
              "Please proceed to payment to complete your registration.",
            icon: "info",
            confirmButtonText: "Proceed to Payment",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then((result) => {
            if (result.isConfirmed && response.data.redirect_url) {
              window.location.href = response.data.redirect_url;
            }
          });
        }

        this.form = this.emptyRegistrationForm();
        this.clearPreview("passport_photo");
        this.clearPreview("attachment_1");
        this.clearPreview("attachment_2");
        this.clearPreview("attachment_3");

        this.selectedCategory = "";
        this.selectedSubCategory = "";
        this.captchaToken = null;

        if (window.grecaptcha) {
          grecaptcha.reset();
        }
      } catch (error) {
        if (error.response && error.response.data) {
          const data = error.response.data;
          const firstError = data.errors
            ? Object.values(data.errors).flat()[0]
            : null;
          Swal.fire(
            firstError || data.message || "Submission failed",
            "",
            "warning"
          );
        } else {
          Swal.fire("Something went wrong. Please try again.", "", "warning");
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.register-upload {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 88px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.register-upload:hover,
.register-upload.is-dragging {
  border-color: #032082;
  background: rgba(3, 32, 130, 0.04);
  box-shadow: 0 0 0 4px rgba(3, 32, 130, 0.06);
}

.register-upload.is-filled {
  border-style: solid;
  border-color: #c7d2fe;
  background: #fff;
}

.register-file-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}

.register-upload-empty,
.register-upload-selected {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 14px;
  padding: 14px 16px;
  margin: 0;
}

.register-upload-empty {
  cursor: pointer;
}

.register-upload-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(3, 32, 130, 0.08);
  color: #032082;
  font-size: 18px;
}

.register-upload-thumb {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  overflow: hidden;
  border-radius: 10px;
  background: #e2e8f0;
}

.register-upload-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.register-upload-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.register-upload-meta strong {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.register-upload-meta span {
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}

.register-upload-remove {
  position: relative;
  z-index: 2;
  margin-left: auto;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  flex-shrink: 0;
}

.register-upload-remove:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.custom-select-choices {
  display: grid;
  gap: 10px;
}

.custom-select-choice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0;
  padding: 12px 14px;
  border: 1.5px solid #dbe3f0;
  border-radius: 12px;
  background: #fff;
  color: #0f172a;
  font-weight: 500;
  line-height: 1.45;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.custom-select-choice input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.custom-select-choice-text {
  position: relative;
  display: block;
  width: 100%;
  padding-left: 28px;
}

.custom-select-choice-text::before {
  content: "";
  position: absolute;
  left: 0;
  top: 3px;
  width: 18px;
  height: 18px;
  border: 1.5px solid #94a3b8;
  border-radius: 999px;
  background: #fff;
}

.custom-select-choice:hover {
  border-color: #032082;
  background: rgba(3, 32, 130, 0.04);
}

.custom-select-choice.is-selected {
  border-color: #032082;
  background: rgba(3, 32, 130, 0.06);
  box-shadow: 0 0 0 4px rgba(3, 32, 130, 0.08);
}

.custom-select-choice.is-selected .custom-select-choice-text::before {
  border-color: #032082;
  background: #032082;
  box-shadow: inset 0 0 0 3px #fff;
}
</style>
